/**
 * Hooks de lecture du pipeline de génération d'exercices (admin).
 *
 * Les tables `pipeline_*` sont protégées par RLS : l'auteur du run et les
 * admins seulement. Les écritures passent par les edge functions
 * (cf. pipeline-mutations.ts).
 */

import type { SupabaseClient } from '@supabase/supabase-js';
import { useQuery } from '@tanstack/react-query';
import type {
  PipelineRun,
  PipelineScenario,
  PipelineMatch,
  PipelineExerciseJob,
  PipelineLlmCall,
  PipelineNotification,
  SchoolLevel,
  ExerciseDifficulty,
} from '@jeraisonne/db';
import { supabase } from '@/lib/supabase';

export type PipelineRunRow = PipelineRun & {
  launcher: { display_name: string | null } | null;
};

/** Tous les runs (actifs en tête), avec le nom de l'auteur. */
export function usePipelineRuns() {
  return useQuery({
    queryKey: ['pipeline-runs'],
    queryFn: async (): Promise<PipelineRunRow[]> => {
      const { data, error } = await supabase
        .from('pipeline_runs')
        .select('*, launcher:profiles(display_name:visible_name)')
        .order('created_at', { ascending: false });
      if (error) throw new Error(error.message);
      return (data ?? []) as unknown as PipelineRunRow[];
    },
  });
}

/** Un run précis. `refetchInterval` permet un suivi live des étapes longues. */
export function usePipelineRun(runId: string, opts?: { live?: boolean }) {
  return useQuery({
    queryKey: ['pipeline-run', runId],
    enabled: !!runId,
    refetchInterval: opts?.live ? 5000 : false,
    queryFn: async (): Promise<PipelineRunRow | null> => {
      const { data, error } = await supabase
        .from('pipeline_runs')
        .select('*, launcher:profiles(display_name:visible_name)')
        .eq('id', runId)
        .maybeSingle();
      if (error) throw new Error(error.message);
      return (data as unknown as PipelineRunRow) ?? null;
    },
  });
}

/** Scénarios d'un run. */
export function usePipelineScenarios(runId: string, opts?: { live?: boolean }) {
  return useQuery({
    queryKey: ['pipeline-scenarios', runId],
    enabled: !!runId,
    refetchInterval: opts?.live ? 4000 : false,
    queryFn: async (): Promise<PipelineScenario[]> => {
      const { data, error } = await supabase
        .from('pipeline_scenarios')
        .select('*')
        .eq('run_id', runId)
        .order('created_at', { ascending: true });
      if (error) throw new Error(error.message);
      return data ?? [];
    },
  });
}

export type PipelineMatchRow = PipelineMatch & {
  chapter: { title: string } | null;
  lesson: { title: string } | null;
};

/** Matchings d'un run, avec titres de chapitre/leçon. */
export function usePipelineMatches(runId: string) {
  return useQuery({
    queryKey: ['pipeline-matches', runId],
    enabled: !!runId,
    queryFn: async (): Promise<PipelineMatchRow[]> => {
      const { data, error } = await supabase
        .from('pipeline_matches')
        .select('*, chapter:chapters(title), lesson:lessons(title)')
        .eq('run_id', runId)
        .order('relevance_score', { ascending: false });
      if (error) throw new Error(error.message);
      return (data ?? []) as unknown as PipelineMatchRow[];
    },
  });
}

export interface ThemePickerOption {
  id: string;
  title: string;
  min_level: SchoolLevel | null;
}

/** Thèmes sélectionnables au lancement d'un pipeline. */
export function useThemesForPicker() {
  return useQuery({
    queryKey: ['pipeline-themes-picker'],
    queryFn: async (): Promise<ThemePickerOption[]> => {
      const { data, error } = await supabase
        .from('themes')
        .select('id, title, min_level')
        .order('title', { ascending: true });
      if (error) throw new Error(error.message);
      return (data ?? []) as ThemePickerOption[];
    },
  });
}

export type PipelineJobRow = PipelineExerciseJob & {
  match: {
    chapter: { title: string } | null;
    lesson: { title: string } | null;
  } | null;
};

/** Jobs de génération d'un run (table live de l'onglet « Jobs »). */
export function usePipelineJobs(runId: string, opts?: { live?: boolean }) {
  return useQuery({
    queryKey: ['pipeline-jobs', runId],
    enabled: !!runId,
    refetchInterval: opts?.live ? 5000 : false,
    queryFn: async (): Promise<PipelineJobRow[]> => {
      const { data, error } = await supabase
        .from('pipeline_exercise_jobs')
        .select('*, match:pipeline_matches(chapter:chapters(title), lesson:lessons(title))')
        .eq('run_id', runId)
        .order('created_at', { ascending: true });
      if (error) throw new Error(error.message);
      return (data ?? []) as unknown as PipelineJobRow[];
    },
  });
}

/** Derniers appels LLM d'un run (onglet « Logs LLM »). */
export function usePipelineLlmCalls(runId: string) {
  return useQuery({
    queryKey: ['pipeline-llm-calls', runId],
    enabled: !!runId,
    queryFn: async (): Promise<PipelineLlmCall[]> => {
      const { data, error } = await supabase
        .from('pipeline_llm_calls')
        .select('*')
        .eq('run_id', runId)
        .order('created_at', { ascending: false })
        .limit(200);
      if (error) throw new Error(error.message);
      return data ?? [];
    },
  });
}

/**
 * Notifications admin envoyées pour un run (mail + push).
 * `live` rafraîchit pendant qu'un run tourne — les notifs arrivent au fil des
 * phases (scénarios prêts, matchings prêts, exercices à relire…).
 */
export function usePipelineNotifications(runId: string, opts?: { live?: boolean }) {
  return useQuery({
    queryKey: ['pipeline-notifications', runId],
    enabled: !!runId,
    refetchInterval: opts?.live ? 5000 : false,
    queryFn: async (): Promise<PipelineNotification[]> => {
      const { data, error } = await supabase
        .from('pipeline_notifications')
        .select('*')
        .eq('run_id', runId)
        .order('created_at', { ascending: false });
      if (error) throw new Error(error.message);
      return (data ?? []) as PipelineNotification[];
    },
  });
}

export interface ReviewChoice {
  display_order: number;
  label_md: string;
  is_correct: boolean;
  error_type: string | null;
  feedback_md: string | null;
}

export interface ReviewQuestion {
  display_order: number;
  prompt_md: string;
  explanation_md: string | null;
  choices: ReviewChoice[];
}

export interface ReviewExercise {
  id: string;
  title: string;
  statement_md: string;
  context_md: string | null;
  difficulty: ExerciseDifficulty;
  estimated_minutes: number | null;
  status: string;
  chapter: { title: string } | null;
  questions: ReviewQuestion[];
}

export interface ReviewBlock {
  id: string;
  exercise_id: string;
  parent_id: string | null;
  block_type: string;
  position: number;
  data: Record<string, unknown> | null;
}

const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v : null);

/**
 * Questions V2 d'un exercice pour la relecture : statement_question → son
 * corrigé (bonne réponse) + ses distracteurs (dont ceux marqués corrects en
 * QCM multiple). Pur, testable. Zéro V1 : plus de lecture d'exercise_steps.
 */
export function reviewQuestionsFromBlocks(blocks: readonly ReviewBlock[]): ReviewQuestion[] {
  const byParent = new Map<string, ReviewBlock[]>();
  for (const b of blocks) {
    if (!b.parent_id) continue;
    const list = byParent.get(b.parent_id) ?? [];
    list.push(b);
    byParent.set(b.parent_id, list);
  }
  const sorted = (xs: ReviewBlock[] | undefined) =>
    [...(xs ?? [])].sort((a, b) => a.position - b.position);
  return blocks
    .filter((b) => b.block_type === 'statement_question')
    .sort((a, b) => a.position - b.position)
    .map((q, i) => {
      const cor = sorted(byParent.get(q.id)).find((b) => b.block_type === 'correction');
      const distractors = sorted(cor ? byParent.get(cor.id) : []).filter(
        (b) => b.block_type === 'distractor',
      );
      const choices: ReviewChoice[] = [];
      if (cor) {
        choices.push({
          display_order: 1,
          label_md: str(cor.data?.answer_md) ?? str(cor.data?.markdown) ?? '',
          is_correct: true,
          error_type: null,
          feedback_md: null,
        });
      }
      distractors.forEach((d, j) =>
        choices.push({
          display_order: j + 2,
          label_md: str(d.data?.label_md) ?? str(d.data?.answer_md) ?? '',
          is_correct: d.data?.is_correct === true,
          error_type: str(d.data?.error_type),
          feedback_md: str(d.data?.feedback_md),
        }),
      );
      return {
        display_order: i + 1,
        prompt_md: str(q.data?.prompt_md) ?? str(q.data?.markdown) ?? '',
        explanation_md: str(cor?.data?.explanation_md),
        choices,
      };
    });
}

/** Exercices générés par un run et encore en attente de relecture. */
export function usePipelineReviewExercises(runId: string) {
  return useQuery({
    queryKey: ['pipeline-review-exercises', runId],
    enabled: !!runId,
    queryFn: async (): Promise<ReviewExercise[]> => {
      const { data: jobs, error: jobsErr } = await supabase
        .from('pipeline_exercise_jobs')
        .select('exercise_id')
        .eq('run_id', runId)
        .eq('status', 'done')
        .not('exercise_id', 'is', null);
      if (jobsErr) throw new Error(jobsErr.message);
      const ids = (jobs ?? []).map((j) => j.exercise_id).filter((x): x is string => !!x);
      if (ids.length === 0) return [];

      const { data, error } = await supabase
        .from('exercises')
        .select(
          `id, title, statement_md, context_md, difficulty, estimated_minutes, status,
           chapter:chapters(title)`,
        )
        .in('id', ids)
        .order('created_at', { ascending: true });
      if (error) throw new Error(error.message);

      // `exercise_blocks` est postérieure aux types générés.
      const { data: blocks, error: blocksErr } = await (supabase as unknown as SupabaseClient)
        .from('exercise_blocks')
        .select('id, exercise_id, parent_id, block_type, position, data')
        .in('exercise_id', ids)
        .in('block_type', ['statement_question', 'correction', 'distractor']);
      if (blocksErr) throw new Error(blocksErr.message);
      const byExercise = new Map<string, ReviewBlock[]>();
      for (const b of (blocks ?? []) as ReviewBlock[]) {
        const list = byExercise.get(b.exercise_id) ?? [];
        list.push(b);
        byExercise.set(b.exercise_id, list);
      }

      return (data ?? []).map((raw): ReviewExercise => {
        const ex = raw as unknown as Omit<ReviewExercise, 'questions'>;
        return { ...ex, questions: reviewQuestionsFromBlocks(byExercise.get(ex.id) ?? []) };
      });
    },
  });
}
